import React from "react";
import {
  AntdLayout,
  AntdHeader,
  AntdContent,
  AntdFooter,
} from "../components/Base/BaseLayout";
import { Header } from "../components/Header";
import type { HeaderPageType } from "../components/Header/Header.types";
import { BottomNavigation } from "../components/BottomNavigation";
import { PullToRefresh } from "../components/Base/PullToRefresh";
import { LayoutProps } from "./Layout.types";
import { useNavigation, ROUTES } from "../utils/navigation";
import "./Layout.scss";
import { useBottomNavInviteTarget } from "../modules/homeDisplay/hooks";
import { usePageConfig } from "../hooks/usePageConfig";

/**
 * 个人中心（/profile）自带顶部渐变区、不需要 Layout 头部的 header variant。
 * template4 沿用 template3 的内嵌式个人中心，故一并列入。
 * template2 的个人中心顶部是自带的用户卡（头像 / VIP / 进度条 / 余额），
 * 再叠一条 Layout 的 logo 栏属于重复，故也列入；template1 仍需要 Layout 头部。
 * VITE_HEADER_VARIANT 由 scripts/preset-plugin.ts 编译期注入。
 */
const HEADER_VARIANTS_WITHOUT_PROFILE_HEADER = new Set([
  "template2",
  "template3",
  "template4",
]);

/**
 * 反过来：这些 **profile** variant 的个人中心明确要 Layout 头部，无论 header variant 是哪套。
 * profile=template4 的顶部是一张身份卡（头像/ID/VIP/余额/快捷入口），不含 logo 栏与全局余额胶囊，
 * 所以需要 Layout 头补上——它与上面那个「按 header variant 关掉」的集合是**取交集时优先生效**的。
 * ⚠️ 判断必须按 profile 而不是改上面那个集合：rosetest / skintest 也是 header=template4，
 * 但它们的 profile 是 template1（自带 Layout 头逻辑），动上面的集合会连带改掉这两个渠道的现状。
 * VITE_PROFILE_VARIANT 同由 preset-plugin 编译期注入。
 */
const PROFILE_VARIANTS_WITH_LAYOUT_HEADER = new Set(["template4"]);

export const Layout: React.FC<LayoutProps> = ({
  children,
  className = "",
  variant = "primary",
  testId,
  cannotRefresh,
}) => {
  const { navigate, getCurrentPath } = useNavigation();
  const inviteTarget = useBottomNavInviteTarget();
  const pageConfig = usePageConfig();
  const handleBottomNavClick = (itemId: string) => {
    const navigationState = { fromBottomNav: true };

    switch (itemId) {
      case "home":
        navigate(ROUTES.HOME, { state: navigationState });
        break;
      case "vip":
        navigate(ROUTES.VIP, { state: navigationState });
        break;
      case "event":
        navigate(ROUTES.EVENT_CENTER, { state: navigationState });
        break;
      case "profile":
        navigate(ROUTES.PROFILE, { state: navigationState });
        break;
      case "agent-center":
        if (inviteTarget !== "INVITE_PAGE") {
          navigate(ROUTES.AGENT_CENTER, { state: navigationState });
        } else {
          navigate(ROUTES.INVITE_FRIENDS, { state: navigationState });
        }
        break;
      default:
        console.log("Unknown navigation item:", itemId);
    }
  };

  const getActiveItem = () => {
    const currentPath = getCurrentPath();
    if (currentPath === ROUTES.EVENT_CENTER) return "event";
    if (currentPath === ROUTES.VIP) return "vip";
    if (currentPath === ROUTES.HOME || currentPath === ROUTES.HOME_ALT)
      return "home";
    if (
      currentPath === ROUTES.INVITE_FRIENDS ||
      currentPath === ROUTES.AGENT_CENTER
    )
      return "agent-center";
    if (currentPath === ROUTES.PROFILE) return "profile";
    return "home";
  };

  const shouldShowHeader = () => {
    const currentPath = getCurrentPath();
    // template3（SuperAce）/ template4 的个人中心是内嵌式设计（页面自带顶部渐变区），
    // 不显示 Layout 头部/logo 栏；template1/template2 的个人中心仍走 Layout 头部。
    // profile variant 的诉求优先：profile=template4 的身份卡不含 logo 栏，需要 Layout 头。
    if (
      currentPath === ROUTES.PROFILE &&
      !PROFILE_VARIANTS_WITH_LAYOUT_HEADER.has(
        import.meta.env.VITE_PROFILE_VARIANT ?? ""
      ) &&
      HEADER_VARIANTS_WITHOUT_PROFILE_HEADER.has(
        import.meta.env.VITE_HEADER_VARIANT ?? ""
      )
    ) {
      return false;
    }
    // VIP 页顶部栏由页面自身的 VipSubpageHeader 常驻渲染（不依赖 Layout / header variant），
    // 故此处不再把 VIP 计入 Layout 头，避免与页面自渲染顶栏重复。
    return (
      currentPath === ROUTES.HOME ||
      currentPath === ROUTES.HOME_ALT ||
      currentPath === ROUTES.PROFILE
    );
  };

  const getHeaderPageType = (): HeaderPageType => {
    const currentPath = getCurrentPath();
    if (currentPath === ROUTES.PROFILE) {
      // profile=template4 要的是「与首页完全同款」的头：pageType="home" 时 header template4 走
      // renderSuperAceHomeHeader（logo + 余额胶囊/充值 + 铃铛），而 pageType="profile" 是另一套
      // 布局（邮件 + 客服，无余额无充值），两者外观差别很大。
      // 同样只对 template4 生效，不动 profile=template1/template2 的现状（az / afun / az-cg）。
      return PROFILE_VARIANTS_WITH_LAYOUT_HEADER.has(
        import.meta.env.VITE_PROFILE_VARIANT ?? ""
      )
        ? "home"
        : "profile";
    }
    return "home";
  };

  const handleRefresh = async () => {
    window.location.reload();
  };

  return (
    <AntdLayout
      id="gbRoot"
      className={`mobile-layout layout-optimized ${
        getCurrentPath() === ROUTES.VIP ? "mobile-layout--vip" : ""
      } ${className}`}
      variant={variant}
      testId={testId}
    >
      {shouldShowHeader() && (
        <AntdHeader className="mobile-layout__header" variant={variant}>
          <Header
            pageType={getHeaderPageType()}
            title={pageConfig[getCurrentPath()]?.title}
            rightIcon={pageConfig[getCurrentPath()]?.icon}
          />
        </AntdHeader>
      )}

      <AntdContent className="mobile-layout__content">
        <PullToRefresh onRefresh={handleRefresh} canRefresh={!cannotRefresh}>
          {children}
        </PullToRefresh>
      </AntdContent>

      <AntdFooter className="mobile-layout__footer" variant={variant}>
        <BottomNavigation
          activeItem={getActiveItem()}
          onItemClick={handleBottomNavClick}
        />
      </AntdFooter>
    </AntdLayout>
  );
};

export default Layout;
